import { useCallback, useEffect, useReducer, useState } from 'react'
import { cn } from '@/lib/utils'
import { refreshIfNeeded } from '@/lib/auth'
import { API_BASE_URL } from '@/lib/config'
import { useAuthStore } from '@/stores/useAuthStore'
import { WavePlayer, type DecodedAudio } from '@/components/ui/WavePlayer'

interface Props {
  phoneNumber: string
  /** L'id numérique du journal d'appel (CallLog). */
  callId: number
  darkMode: boolean
  /** Durée connue du serveur (secondes), affichée avant le chargement. */
  dureeConnue?: number | null
  /** Dans une carte d'appel : fond léger, sans bordure propre. */
  integre?: boolean
  /** Appel pris par un agent IA : l'audio vient de son proxy (AICallLog). */
  aiCallId?: number | null
}

/**
 * Un seul son par appel, partagé par tous ses lecteurs (la carte du fil et le
 * panneau « Détails ») : même lecture, même pause, même position, et une
 * seule forme d'onde téléchargée. Lancer un appel met les autres en pause.
 * Le son est lâché quand plus aucun lecteur ne l'affiche.
 */
interface Son {
  audio: HTMLAudioElement
  decode: DecodedAudio | null
  abonnes: Set<() => void>
}
const sons = new Map<number, Son>()

function son(callId: number): Son {
  let s = sons.get(callId)
  if (!s) {
    const audio = new Audio()
    audio.addEventListener('play', () => mettreEnPauseLesAutres(callId))
    s = { audio, decode: null, abonnes: new Set() }
    sons.set(callId, s)
  }
  return s
}

function mettreEnPauseLesAutres(callId: number) {
  sons.forEach((s, id) => {
    if (id !== callId && !s.audio.paused) s.audio.pause()
  })
}

function lacher(callId: number, s: Son) {
  s.audio.pause()
  if (s.audio.src.startsWith('blob:')) URL.revokeObjectURL(s.audio.src)
  s.audio.removeAttribute('src')
  s.audio.load()
  sons.delete(callId)
}

/**
 * L'enregistrement d'un appel, écoutable depuis le fil et depuis le panneau
 * « Détails ». Forme d'onde chargée quand la carte arrive à l'écran.
 */
export function LecteurAppel({ phoneNumber, callId, darkMode, dureeConnue, integre = false, aiCallId = null }: Props) {
  const [s, setS] = useState<Son | null>(null)
  const [, rafraichir] = useReducer((n: number) => n + 1, 0)

  useEffect(() => {
    const x = son(callId)
    x.abonnes.add(rafraichir)
    setS(x)
    return () => {
      x.abonnes.delete(rafraichir)
      if (x.abonnes.size === 0) lacher(callId, x)
    }
  }, [callId])

  // Construite au moment du chargement, avec un jeton renouvelé au besoin : une
  // carte qui arrive à l'écran longtemps après l'ouverture du fil ne doit pas
  // partir avec un jeton expiré.
  const url = useCallback(async () => {
    await refreshIfNeeded()
    const jeton = useAuthStore.getState().accessToken
    if (!jeton) return undefined
    return aiCallId
      ? `${API_BASE_URL}/api/ai-agents/calls/${aiCallId}/audio/?token=${encodeURIComponent(jeton)}`
      : `${API_BASE_URL}/api/contacts/${encodeURIComponent(phoneNumber)}/timeline/${callId}/recording/audio/?token=${encodeURIComponent(jeton)}`
  }, [phoneNumber, callId, aiCallId])

  const decode = (d: DecodedAudio) => {
    if (!s || s.decode) return
    s.decode = d
    s.abonnes.forEach((f) => f())
  }

  return (
    <div
      className={cn(
        'flex items-center rounded-lg px-2.5 py-1.5 w-full',
        integre
          ? darkMode ? 'bg-slate-900/60' : 'bg-gray-50'
          : darkMode ? 'border border-slate-800 bg-slate-900' : 'border border-gray-200 bg-white',
      )}
    >
      <WavePlayer
        key={callId}
        audioUrl={s ? url : undefined}
        media={s?.audio}
        decoded={s?.decode}
        knownDuration={dureeConnue}
        onDecoded={decode}
        lazy
        compact
      />
    </div>
  )
}
